import { TagProps } from "../../components/visuals/CompassTag";
import { BadgeStatus } from "../../types/StatusBadgeType";
import { MESSAGES } from "../../messages";
import {
  ColumnType as CustomColumnType,
  TaskDataSource,
} from "../../models/tableModels";
import { CellType, TableCellType } from "../../models/tableModels";
import {
  CreateTaskConfig,
  CreateTaskParams,
  NoteDropdownProps,
  NoteSummary,
  Task,
  TaskPriority,
  TaskStatus,
  TaskTrail,
  TrailPathItem,
  UpdateTaskConfig,
} from "../../models/workflowModels";
import { statusBadgeColors } from "../../styles";
import {
  formatReadableDate,
  formatRelativeTime,
} from "../../utils/formattingUtils";
import { STATUS_MAPPING, TITLE_TAG_COLORS_MAPPING } from "./constants";
import gaspAvatar from "../../assets/images/gaspard_avatar.png";
import { formatReadable as beautifyText } from "../../utils/formattingUtils";
import { TitleWithCircleTagProps } from "../../components/tables/TitleWithCircleTag";
import { NULLABLE_VALUES } from "../../constants";
import { resolveDisplayName } from "../../constants";
import penIcon from "../../assets/svg/pen.svg";
import { Filter } from "../../types/PageFilterTypes";
import { getStatusBadgeOption } from "../../components/visuals/StatusBadge";

export const getColumnTypeFromKeyOrValue = (
  column: string,
  value: CellType,
  columnTypes: CustomColumnType[] = [],
  loading = false,
): CustomColumnType => {
  if (loading) return { columnKey: column, type: TableCellType.LOADING };
  if (value === MESSAGES.NOT_AVAILABLE)
    return { columnKey: column, type: TableCellType.TEXT };

  for (const columnType of columnTypes) {
    if (columnType.columnKey === column) {
      return columnType;
    }
  }

  // Fallback based on the type of value
  if (typeof value === "number") {
    return { columnKey: column, type: TableCellType.NUMBER };
  }
  return { columnKey: column, type: TableCellType.TEXT };
};

export const buildTaskDataSource = (
  data: Task[],
  onDelete?: (task_id: string | number) => void,
): TaskDataSource[] => {
  return data.map((task) => {
    const title = beautifyText(task.title);
    const description = beautifyText(task.description);
    return {
      key: task.task_id,
      title: buildTitleProps(task.task_id, title, task.is_flag),
      priority: task.priority,
      description: description,
      status: buildStatusTagProps(task.status),
      note_count: buildNoteProps(task.notes, task.task_id),
      date_created: formatReadableDate(task.date_created),
      date_due: buildDateDueTagProps(task.date_due),
      date_completed: NULLABLE_VALUES.includes(task.date_completed)
        ? task.date_completed
        : formatReadableDate(task.date_completed),
      assigned_to: {
        username: task.assigned_to,
        image:
          task.assigned_to === "gaspard@gaspardcorporation" ? gaspAvatar : null,
        displayText: resolveDisplayName(task.assigned_to),
      },
      last_updated: {
        username: task.performed_by,
        image:
          task.performed_by === "gaspard@gaspardcorporation"
            ? gaspAvatar
            : null,
        displayText: formatRelativeTime(task.last_modified),
      },
      delete: {
        id: task.task_id,
        isDeletable: true,
        onDelete,
      },
      // Raw fields for client-side filtering and sorting
      _rawStatus: task.status,
      _rawDateDue: task.date_due,
      _rawLastModified: task.last_modified,
    };
  });
};

const buildNoteProps = (
  notes: NoteSummary[] | null[],
  taskId: string,
): NoteDropdownProps | "-" => {
  if (!notes || !notes.length || !notes[0]?.note_id) {
    notes = [];
  }

  const noteCount = notes ? notes.length : 0;

  return {
    title: `${noteCount} note${noteCount === 1 ? "" : "s"}`,
    icon: penIcon,
    notes: notes as NoteSummary[],
    taskId,
  };
};

export const buildStatusTagProps = (status: TaskStatus): TagProps => {
  return {
    tagKey: STATUS_MAPPING[status],
    color: statusBadgeColors[STATUS_MAPPING[status]].text,
    bg: statusBadgeColors[STATUS_MAPPING[status]].bg,
    displayValue: "",
    shouldDisplayKey: true,
  };
};

export const buildDateDueTagProps = (dateDue: string): TagProps | "-" => {
  if (NULLABLE_VALUES.includes(dateDue)) return "-";

  const isDue = new Date(dateDue) < new Date();
  const colors = isDue
    ? statusBadgeColors[STATUS_MAPPING[TaskStatus.STUCK]]
    : statusBadgeColors[STATUS_MAPPING[TaskStatus.NOT_STARTED]];
  return {
    tagKey: formatReadableDate(dateDue),
    color: colors.text,
    bg: colors.bg,
    displayValue: "",
    shouldDisplayKey: true,
  };
};

export const buildTitleProps = (
  taskId: string,
  title: string,
  isFlag: boolean,
): TitleWithCircleTagProps => {
  const type = isFlag ? "F" : "T";
  const bg = statusBadgeColors[TITLE_TAG_COLORS_MAPPING[type]].bg;
  const color = statusBadgeColors[TITLE_TAG_COLORS_MAPPING[type]].text;

  return {
    taskId,
    title,
    bg: bg,
    color: color,
    type,
  };
};

export const generateMockTaskData = (): TaskDataSource[] => {
  const mockData: TaskDataSource[] = [];

  for (let i = 0; i < 8; i++) {
    const task: Task = {
      task_id: (i + 1).toString(),
      title: `Task ${i + 1}`,
      is_flag: i % 2 === 0,
      priority: TaskPriority.HIGH,
      description: `Description for Task ${i + 1}`,
      status: TaskStatus.IN_PROGRESS,
      notes: [],
      date_created: new Date().toISOString(),
      date_due: new Date().toISOString(),
      date_completed: "-",
      assigned_to: "John Doe",
      performed_by: "Jane Smith",
      last_modified: new Date().toISOString(),
      created_by: "Jane Smith",
      subject_type: "Task",
      is_archived: false,
    };

    mockData.push(buildTaskDataSource([task])[0]);
  }

  return mockData;
};

export const convertConfigToParams = (
  config: CreateTaskConfig,
): CreateTaskParams | null => {
  if (
    !config.title ||
    !config.priority ||
    !config.assignee ||
    !config.subjectType
  ) {
    console.error("Missing required fields in CreateTaskConfig");
    return null;
  }

  return {
    title: config.title,
    priority: config.priority,
    date_due: config.dueDate || undefined,
    assigned_to: config.assignee,
    subject_type: config.subjectType,
    subject_ids: config.subjects || undefined,
  };
};

export const convertConfigToUpdateParams = (
  config: CreateTaskConfig,
  taskId: string,
  status: TaskStatus,
): UpdateTaskConfig | null => {
  if (!config.title || !config.priority || !config.assignee || !status) {
    console.error("Missing required fields in UpdateTaskConfig");
    return null;
  }

  const dateDue = ["Select due date", MESSAGES.NOT_AVAILABLE].includes(
    config.dueDate as string,
  )
    ? undefined
    : config.dueDate;

  const updateConfig: UpdateTaskConfig = {
    task_id: taskId,
    title: config.title,
    priority: config.priority,
    assigned_to: config.assignee,
    status,
  };

  return dateDue ? { ...updateConfig, date_due: dateDue } : updateConfig;
};

export const buildBadgeStatusOptions = (): Filter[] => {
  return Object.keys(STATUS_MAPPING).map((key) => ({
    key,
    displayValue: STATUS_MAPPING[key as TaskStatus],
    component: () =>
      getStatusBadgeOption(STATUS_MAPPING[key as TaskStatus] as BadgeStatus),
  }));
};

const DETAIL_LABEL_MAP: Record<string, string> = {
  status: "Status",
  priority: "Priority",
  title: "Title",
  assigned_to: "Assigned To",
  date_due: "Due Date",
  subject_type: "Subject Type",
  note_title: "Note Title",
};

/** Keys that are metadata/IDs — never shown as detail entries */
const isIdKey = (key: string): boolean =>
  key === "id" || key.endsWith("_id") || key.endsWith("_ids");

/**
 * Detect note events robustly — don't rely solely on action_type matching
 * since the API may return a variant casing or different type string.
 */
const isNoteAction = (trail: TaskTrail): boolean => {
  // Exact match
  if (trail.action_type === "ADD_TASK_NOTE") return true;
  // Case-insensitive match
  if (trail.action_type?.toUpperCase() === "ADD_TASK_NOTE") return true;
  // Presence of note-specific fields in details
  if (trail.details?.note_title || trail.details?.note_id) return true;
  // Content-based detection from action_details
  if (/note\s+with\s+title/i.test(trail.action_details)) return true;
  return false;
};

/** Extract note ID from trail data */
const extractNoteId = (trail: TaskTrail): string | undefined => {
  if (trail.details?.note_id) return trail.details.note_id;
  // For ADD_TASK_NOTE events, the log_id is the note identifier
  if (trail.log_id) return trail.log_id;
  return undefined;
};

/** Build detail entries showing only fields that changed vs previous state */
const buildDiffEntries = (
  details: Record<string, string>,
  prevDetails: Record<string, string> | null,
): { label: string; value: string }[] => {
  return Object.entries(details)
    .filter(([key, value]) => {
      if (isIdKey(key)) return false;
      if (value === undefined || value === null || value === "") return false;
      // If there's a previous state, only include fields that actually changed
      if (prevDetails && prevDetails[key] === value) return false;
      return true;
    })
    .map(([key, value]) => ({
      label: DETAIL_LABEL_MAP[key] || beautifyText(key.replace(/_/g, " ")),
      value: DETAIL_LABEL_MAP[value as string]
        ? DETAIL_LABEL_MAP[value as string]
        : beautifyText(String(value)),
    }));
};

/** For note events, extract just the note title from details */
const getNoteDisplayText = (trail: TaskTrail): string => {
  // Primary: use note_title from details
  if (trail.details?.note_title) {
    return trail.details.note_title;
  }
  // Fallback: parse title from action_details
  const match = trail.action_details.match(/title:\s*(.+?)(?:\s+added\b|$)/i);
  if (match) return match[1].trim();
  // Last resort: strip IDs and metadata from action_details
  return stripIdsFromText(trail.action_details);
};

/** Remove UUIDs, hex IDs, and "for task: <id>" patterns from display text */
const stripIdsFromText = (text: string): string => {
  return text
    .replace(/\bfor\s+task:\s*[a-f0-9-]+/gi, "") // "for task: abc123..."
    .replace(/\b[a-f0-9]{24,}\b/gi, "") // long hex IDs (24+ chars)
    .replace(
      /\b[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}\b/gi,
      "",
    ) // UUIDs
    .replace(/\s{2,}/g, " ") // collapse multiple spaces
    .trim();
};

export const buildTrailPath = (taskTrail: TaskTrail[]): TrailPathItem[] => {
  const path: TrailPathItem[] = [];
  let lastDate = "";
  let prevDetails: Record<string, string> | null = null;

  taskTrail.forEach((trail) => {
    let lastStatus: TaskStatus = TaskStatus.NOT_STARTED;

    const retrievedStatus = getTrailStatus(trail.details);
    if (retrievedStatus) {
      lastStatus = retrievedStatus;
    }

    const currentDate = trail.timestamp.slice(0, 10);
    const isNote = isNoteAction(trail);
    const noteId = isNote ? extractNoteId(trail) : undefined;

    const displayText = isNote
      ? getNoteDisplayText(trail)
      : stripIdsFromText(trail.action_details);

    if (path.length > 0 && currentDate === lastDate && isNote) {
      const prev = path[path.length - 1];
      prev.noteIds = Array.from(
        { length: prev.children.length },
        (_, i) => prev.noteIds[i] || "",
      );
      prev.children.push(displayText);
      prev.noteIds.push(noteId || "");
    } else {
      // For UPDATE events, diff against previous trail entry
      const isUpdate =
        trail.action_type === "UPDATE_TASK" ||
        trail.action_type?.toUpperCase() === "UPDATE_TASK";
      const detailEntries = isUpdate
        ? buildDiffEntries(trail.details, prevDetails)
        : buildDiffEntries(trail.details, null);

      path.push({
        date: currentDate,
        timestamp: trail.timestamp,
        status: lastStatus,
        color: buildStatusTagProps(lastStatus).color,
        performedBy: trail.performed_by,
        actionType: trail.action_type,
        isNoteEvent: isNote,
        children: [displayText],
        detailEntries,
        noteIds: noteId ? [noteId] : [],
      });
      lastDate = currentDate;
    }

    // Track previous details for diffing
    prevDetails = { ...trail.details };
  });

  return path;
};

export const getTrailStatus = (
  details: Record<string, string>,
): TaskStatus | false => {
  if (details?.status) {
    return TaskStatus[details.status as keyof typeof TaskStatus];
  }
  return false;
};
